import styles from './stockList.module.css'
import { List, FieldType, UIPermission, Action, Column, StockAction, FieldValue, Data, SpinnerDiv, Badge, O, Async } from "../../../../features/ui"
import { useSystemStateHooks } from "../../../system"
import { define } from "../../../../utils/typeUtils"
import { DataType } from "../../../../tables"
import { useStockHooks } from '../../hooks/stockHooks'
import { BranchStockTables } from '../branch-stock-tables/branchStockTables'
import { StockFilters } from './stockFilters'
import { useState } from 'react'
import { Stock } from '../../state/types'
import { useNavigate } from 'react-router-dom'
import { Product } from '../../../products'
import { useSupplierLoaders } from '../../../purchases'
import { RoutePath } from '../../../../features/routes'

type Props = {
    stocks: Stock[]
    products: Product[]
}

export const StockList = (props: Props) => {
    const { stocks, products } = props

    const [rows, setRows] = useState<Data[]>([])
    const [asyncDataLoaded, setAsyncDataLoaded] = useState(false)

    const { describeAvailableStock, describeReservedStock, describeTotalStock, describeSoldWithoutStock, getNoLotStock, getStockActionRoute } = useStockHooks()
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_STOCK)
    const navigate = useNavigate()

    const supplierLoaders = useSupplierLoaders()

    const findStock = (stockId?: string) => define(stocks.find(stock => stock.id == stockId))
    
    const findProduct = (productId?: string) => products.find(product => product.id === productId)

    const columns: Column[] = [
        {
            name: 'productImageUrl',
            type: FieldType.IMAGE,
            label: ' '
        },
        {
            name: 'productCode',
            type: FieldType.TEXT,
            label: 'Código',
            render: (productCode?: FieldValue) => `#${productCode}`
        },
        {
            name: 'productId',
            type: FieldType.TEXT,
            label: 'Producto',
            render: (productId?: FieldValue) => {
                const product = findProduct(productId as O<string>)
                return <>{define(product).name}</>
            }
        },
        {
            name: 'productSupplier',
            type: FieldType.TEXT,
            label: 'Proveedor',
            render: (supplierId?: FieldValue) => <Async
                loadFn={async () => {
                    const supplier = await supplierLoaders().loadSupplier(define(supplierId) as string)
                    return <Badge label={define(supplier).name} />
                }}
                deps={stocks}
            />
        },
        {
            name: 'availableUnits',
            type: FieldType.NUMBER,
            label: 'Disponible',
            render: (stockId?: FieldValue) => <Async
                loadFn={async () => {
                    const stock = findStock(stockId as O<string>)
                    const product = findProduct(stock.productId)
                    const soldWithoutStock = await describeSoldWithoutStock(define(product), stock.branchId)
                    return <>
                        {await describeAvailableStock(define(product), stock.branchId)}
                        {soldWithoutStock && <span className="d-block small text-danger">{soldWithoutStock} vendido sin stock</span>}
                    </>
                }}
            />
        },
        {
            name: 'reservedUnits',
            type: FieldType.NUMBER,
            label: 'Reservado',
            render: (stockId?: FieldValue) => <Async
                loadFn={async () => {
                    const stock = findStock(stockId as O<string>)
                    const product = findProduct(stock.productId)
                    return <>{await describeReservedStock(define(product), stock.branchId)}</>
                }}
            />
        },
        {
            name: 'totalUnits',
            type: FieldType.NUMBER,
            label: 'Total',
            render: (stockId?: FieldValue) => <Async
                loadFn={async () => {
                    const stock = findStock(stockId as O<string>)
                    const product = findProduct(stock.productId)
                    return <>{await describeTotalStock(define(product), stock.branchId)}</>
                }}
            />
        }
    ]

    const onView = async (productId?: string) => {
        const baseStock = await getNoLotStock(define(productId))
        navigate(baseStock.id)
    }

    const onEdit = async (stockAction: StockAction, productId?: string) => {
        const baseStock = await getNoLotStock(define(productId))
        const actionPath = getStockActionRoute(stockAction)
        navigate(`${baseStock.id}/${actionPath}`)
    }

    const onIncreaseStock = (productId?: string) => onEdit(StockAction.ADD, productId)
    const onDecreaseStock = (productId?: string) => onEdit(StockAction.SUBSTRACT, productId)
    
    const onTransform = async (productId?: string) => {
        const baseStock = await getNoLotStock(define(productId))
        navigate(`${baseStock.id}/${RoutePath.TRANSFORM}`)
    }

    const actions = (productId: string): Action[] => {
        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', onClick: onView },
        ] 
        if (!readOnly) {
            actionList.push(
                { icon: 'plus-circle-fill', tooltip: 'Alta manual', onClick: onIncreaseStock },
                { icon: 'dash-circle-fill', kind: 'secondary', tooltip: 'Baja manual', onClick: onDecreaseStock }
            )
            const product = findProduct(productId)
            if (product?.equivalences && product.equivalences.length > 0) {
                actionList.splice(1, 0, { icon: 'arrow-left-right', tooltip: 'Transformar', onClick: onTransform })
            }
        }

        return actionList
    }

    const renderDetail = (row: Data) => (
        <div className={styles.detailContainer}>
            <BranchStockTables productId={define(row.productId) as string} />
        </div>
    )

    const setRowsState = (stockList: Stock[]) => {
        const rowsMap = stockList.reduce((map, stock) => {
            const product = define(findProduct(stock.productId))
            const row = map.get(product.id)
            
            if (!row) {
                const newRow: Data = {
                    productImageUrl: product.imageUrl,
                    productCode: product.code,
                    productId: product.id,
                    productSupplier: product.supplierId,
                    availableUnits: stock.id,
                    reservedUnits: stock.id,
                    totalUnits: stock.id
                }
                map.set(product.id, newRow)
            }
    
            return map
        }, new Map<string, Data>())
        
        setRows(Array.from(rowsMap.values()))
    }

    const renderAsync = (element: JSX.Element) => asyncDataLoaded ? element : <SpinnerDiv />

    const onSearchStart = () => setAsyncDataLoaded(false)

    const onSearchDone = (dataTypes: DataType[]) => {
        setRowsState(dataTypes as Stock[])
        setAsyncDataLoaded(true)
    }

    return (
        <>
            <StockFilters
                stocks={stocks}
                onSearchStart={onSearchStart}
                onSearchDone={onSearchDone}
            />
            {renderAsync(
                <List
                    columns={columns}
                    rows={rows}
                    rowId="productId"
                    actions={actions}
                    readOnly={readOnly}
                    renderDetail={renderDetail}
                />
            )}
        </>
    )
}
